import styles from "../../../scss/Form.module.scss";
import useFormErrorHandling from "hooks/useFormErrorHandling";
import { toast } from "react-toastify";
import { Field } from "components/Shared";
import { Email } from "@mui/icons-material";
import { SaveIcon } from "components/Layout/MainLayout/Sidebar/icons/SaveIcon";
import { useEffect, useRef, useState } from "react";
import { UserStatus } from "components/Shared/UserStatus";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSendActivationEmailMutation } from "redux/api/userApi";
import { FormProvider, SubmitHandler, useForm, useWatch } from "react-hook-form";
import { Box, Card, CardContent, Typography, Button, Grid } from "@mui/material";
import { EMAIL_ERROR_TOAST, EMAIL_SEND_TOAST, FIELDS, initialValues, IUserForm, IUserFormProps, ROLE_OPTIONS, userFormSchema } from "./Form.config";
import { useGetPointOfSaleByCompanyQuery } from "redux/api/pointOfSaleApi";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { useAppSelector } from "redux/store";
import { IUserLoggedState } from "redux/features/loggedUserSlice";
import { UserRoles } from "Enums/UserRoles";
import { useIntl } from "react-intl";


// Image field height configuration

const IMAGE_HEIGHT = "9rem";

export const Form = (props: IUserFormProps) => {
  const { formatMessage } = useIntl();

  const { onSubmit, createApiError, editApiError, currentUser, companies } = props;

  const { user } = useAppSelector(
    (state: { userLoggedState: IUserLoggedState }) => state.userLoggedState
  );
  const filteredRoleOptions = () => {
    const formattedOptions = ROLE_OPTIONS.map(option => ({
      label: option.label,
      value: formatMessage({ id: option.value }),
    }));
  
    if (user?.role === UserRoles.SUPERADMIN) {
      // Retorna todas las opciones si es SUPERADMIN
      return formattedOptions;
    } else if (user?.role === UserRoles.ADMIN) {
      // Retorna opciones excluyendo SUPERADMIN si es ADMIN
      return formattedOptions.filter(option => option.label !== UserRoles.SUPERADMIN);
    }
    // Para otros roles, excluye tanto ADMIN como SUPERADMIN
    return formattedOptions.filter(
      option => option.label !== UserRoles.SUPERADMIN && option.label !== UserRoles.ADMIN
    );
  };



  const [selectedCompany, setSelectedCompany] = useState<string>(currentUser?.companyId || "");
  // API call to send activation mail

  const [sendActivationEmail, { isSuccess: isEmailSendSuccess, isError: isEmailSendError }] = useSendActivationEmailMutation();

  //API CALL TO FETCH POINTS OF SALE

  const { data: pointsOfSaleData, isLoading: isLoadingPointsOfSale } = useGetPointOfSaleByCompanyQuery(selectedCompany || skipToken);
  // Form configuration

  const methods = useForm<IUserForm>({
    resolver: zodResolver(userFormSchema),
    defaultValues: initialValues(currentUser, user ?? undefined),
  });
  useEffect(() => {
    // Solo establecer selectedCompany automáticamente si no se está editando un usuario
    if (!currentUser) {
      setSelectedCompany(user?.companyId || "");
    }
  }, [user?.companyId, currentUser]);
  // Handle company change

  const watchedCompany = useWatch({
    control: methods.control,
    name: FIELDS.COMPANY.FIELD,
  });

  // Referencia para el valor actual
  const prevCompanyRef = useRef(watchedCompany);

  useEffect(() => {
    // Compara el valor actual con el valor anterior almacenado en la referencia
    if (watchedCompany !== prevCompanyRef.current) {
      setSelectedCompany(watchedCompany || "");
      prevCompanyRef.current = watchedCompany; // Actualiza la referencia
    }
  }, [watchedCompany]);

  // Form submit handler

  const onSubmitHandler: SubmitHandler<IUserForm> = (values: IUserForm) => {
    const formData = new FormData();

    if (values.image) formData.append("image", values.image as Blob);
    formData.append("data", JSON.stringify(values));

    onSubmit(formData);
  };

  // Email credentials success handler

  useEffect(() => {
    if (isEmailSendSuccess) {
      toast.success(EMAIL_SEND_TOAST);
    }
  }, [isEmailSendSuccess]);

  // Email credentials error handler

  useEffect(() => {
    if (isEmailSendError) {
      toast.error(EMAIL_ERROR_TOAST);
    }
  }, [isEmailSendError]);

  // Function execute api call

  const onSendActivationEmailHandler = (userId: string) => {
    sendActivationEmail(userId);
  }

  // Form error handlers (on creation and edition)

  useFormErrorHandling(methods.setError, editApiError);
  useFormErrorHandling(methods.setError, createApiError);

  function handleCompanyChange(value: string) {
    setSelectedCompany(value);
  }

  return (
    <Card>
      <CardContent className={styles.cardContent}>
        <FormProvider {...methods}>
          <Box
            component="form"
            noValidate
            autoComplete="off"
            onSubmit={methods.handleSubmit(onSubmitHandler)}
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {currentUser && (
                  <Box width={"100%"} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                    <Typography
                      fontWeight={700}
                      fontSize={"16px"}
                      color={"#313131"}
                      sx={{ marginRight: '16px' }}
                    >
                      {currentUser.name} {currentUser.lastName}
                    </Typography>
                    <UserStatus user={currentUser} />
                  </Box>
                )}
              </Grid>
              <Grid item xs={6}>
                {" "}
                <Field.Image
                  image={currentUser?.image}
                  name={FIELDS.IMAGE.FIELD}
                  height={IMAGE_HEIGHT}
                  cssImage={{
                    width: "12.5rem",
                    height: IMAGE_HEIGHT,
                    objectFit: "cover",
                    objectPosition: "center",
                  }}
                  imageLoading={false}
                />

              </Grid>
              <Grid item xs={6}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Field.TextField
                      name={FIELDS.NAME.FIELD}
                      label={formatMessage({ id: FIELDS.NAME.LABEL })}
                      defaultValue={currentUser?.name}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Field.TextField
                      name={FIELDS.LASTNAME.FIELD}
                      label={formatMessage({ id: FIELDS.LASTNAME.LABEL })}
                      defaultValue={currentUser?.lastName}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={6}>
                <Field.TextField
                  name={FIELDS.EMAIL.FIELD}
                  label={formatMessage({ id: FIELDS.EMAIL.LABEL })}
                  defaultValue={currentUser?.email}
                  type="email"
                />
              </Grid>
              <Grid item xs={6}>
                <Field.Select
                  control={methods.control}
                  name={FIELDS.ROLE.FIELD}
                  label={formatMessage({ id: FIELDS.ROLE.LABEL })}
                  defaultValue=""
                  multiple={false}
                  helperText={""}
                  fieldValue="label"
                  fieldName="value"
                  variant="outlined"
                  disabled={false}
                  options={filteredRoleOptions()}
                />
              </Grid>
              <Grid item xs={5}>
                <Field.Select
                  control={methods.control}
                  name={FIELDS.COMPANY.FIELD}
                  label={formatMessage({ id: FIELDS.COMPANY.LABEL })}
                  defaultValue={companies.find((company) => company.value === currentUser?.companyId)}
                  multiple={false}
                  helperText={""}
                  fieldValue="value"
                  fieldName="label"
                  variant="outlined"
                  options={companies}
                  disabled={!!currentUser || user?.role !== UserRoles.SUPERADMIN}                  

                />
              </Grid>
              <Grid item xs={7}>
                <Field.Select
                  control={methods.control}
                  name={FIELDS.POINT_OF_SALE.FIELD}
                  label={formatMessage({ id: FIELDS.POINT_OF_SALE.LABEL })}
                  defaultValue=""
                  multiple={false}
                  helperText={""}
                  fieldValue="value"
                  fieldName="label"
                  variant="outlined"
                  options={pointsOfSaleData?.map((pointOfSale) => ({ value: pointOfSale.id, label: pointOfSale.name }))}
                  disabled={!selectedCompany || isLoadingPointsOfSale}
                />
              </Grid>
            </Grid>

            {methods.formState.errors &&
              methods.formState.errors.general?.message && (
                <>{methods.formState.errors.general.message}</>
              )}

            <Box display="flex" justifyContent="flex-end" gap={"16px"} sx={{ mt: 3 }}>
              {currentUser && !currentUser?.verified && currentUser?.id && (
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => {
                    onSendActivationEmailHandler(currentUser?.id);
                  }}
                  sx={{ width: "250px", padding: "0px !important" }}
                  startIcon={<Email />}
                >
                  <Typography
                    fontWeight={500}
                    fontSize={"16px"}
                    alignContent="center"
                  >
                  {formatMessage({ id: "sendCredentials" })}
                  </Typography>
                </Button>
              )}
              <Button variant="contained" color="primary" type="submit" startIcon={<SaveIcon />}>
                <Typography
                  fontWeight={500}
                  fontSize={"16px"}
                  color="#FFF"
                  alignContent="center"
                >
                  {formatMessage({ id: "save" })}
                </Typography>
              </Button>
            </Box>
          </Box>
        </FormProvider>
      </CardContent>
    </Card>
  );
};